/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation,
  DOCUMENT
} from '@angular/core';
import {
  animate,
  AnimationBuilder,
  AnimationPlayer,
  style,
} from '@angular/animations';

import { Subject, takeUntil } from 'rxjs';

import { MediaObserver } from '@angular/flex-layout';

import { CoreConfigService, CoreMediaService } from '../../../services';

import { CoreSidebarService } from './services/core-sidebar.service';

@Component({
  selector: 'xbaht-core-sidebar',
  templateUrl: './core-sidebar.component.html',
  styleUrl: './core-sidebar.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class CoreSidebarComponent implements OnInit, OnDestroy {
  @Input()
  public name!: string;

  @Input()
  public overlayClass!: string;

  public isOpened: boolean;

  @Input()
  public collapsibleSidebar!: string;

  public iscollapsibleSidebar!: boolean;

  @HostBinding('class.expanded')
  public expanded!: boolean;

  @Output()
  public collapsedChangedEvent: EventEmitter<boolean>;

  @Output()
  public openedChangedEvent: EventEmitter<boolean>;

  @Input()
  public overlayVisibility: boolean;

  @Input()
  public hideOnEsc: boolean;

  @HostListener('document:keydown.escape', ['$event'])
  public onKeydownHandler(event: Event) {
    if (this.hideOnEsc) {
      this.close();
    }
  }

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    // Expand the sidebar temporarily
    this.expandTemporarily();
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    this.collapseTemporarily();
  }

  public menuClass?: string;

  public rootElement: any;

  private _coreConfig: any;
  private _collapsed: boolean;
  private _wasCollapsible?: boolean;
  private _wasCollapsed?: boolean;
  private _animationPlayer?: AnimationPlayer;
  private _overlay: HTMLElement | null = null;

  private _unsubscribe: Subject<void>;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    private _coreConfigService: CoreConfigService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _animationBuilder: AnimationBuilder,
    private _mediaObserver: MediaObserver
  ) {
    this.isOpened = false;
    this.overlayVisibility = true;
    this.hideOnEsc = false;

    this.rootElement =
      this.document.querySelectorAll('.vertical-layout')[0] ||
      this.document.querySelectorAll('.horizontal-layout')[0];

    this.collapsedChangedEvent = new EventEmitter();
    this.openedChangedEvent = new EventEmitter();

    this._collapsed = false;
    this._unsubscribe = new Subject<void>();
  }

  @Input()
  public set collapsed(value: boolean) {
    this._collapsed = value;

    if (!this.isOpened) {
      this.rootElement.classList.add('menu-expanded');
      return;
    }

    if (value) {
      this.collapse();

      this.rootElement.classList.add('menu-collapsed');
      this.rootElement.classList.remove('menu-expanded');
    } else {
      this.expand();

      this.rootElement.classList.add('menu-expanded');
      this.rootElement.classList.remove('menu-collapsed');
    }

    this.collapsedChangedEvent.emit(this.collapsed);
  }

  public get collapsed(): boolean {
    return this._collapsed;
  }

  ngOnInit(): void {
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((config: any) => {
        this._coreConfig = config;
        if (config.layout.type == 'vertical') {
          this.menuClass = 'vertical-menu-modern';
        } else {
          this.menuClass = 'horizontal-menu';
        }
      });

    this._coreSidebarService.setSidebarRegistry(this.name, this);

    this._setupCollapsibleSidebar();

    this._defaultCollapsed();
  }

  ngOnDestroy(): void {
    if (this.collapsed) {
      this.expand();
    }

    this._coreSidebarService.removeSidebarRegistry(this.name);

    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  private _setupCollapsibleSidebar(): void {
    if (!this.collapsibleSidebar) {
      return;
    }

    this._wasCollapsible = false;

    this._wasCollapsed = this.collapsed;

    this._coreMediaService.onMediaUpdate
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        const isCollapsible = this._mediaObserver.isActive(
          this.collapsibleSidebar
        );
        this._wasCollapsed =
          this._coreConfig.layout.menu.collapsed || this.collapsed;

        if (!isCollapsible && this.name === 'menu') {
          this.rootElement.classList.remove(this.menuClass);
          this.rootElement.classList.add('vertical-overlay-menu');
        }

        if (this._wasCollapsible === isCollapsible) {
          return;
        }

        if (isCollapsible) {
          this.iscollapsibleSidebar = true;

          this.isOpened = true;

          this.expanded = true;

          this.openedChangedEvent.emit(this.isOpened);

          if (this._wasCollapsed) {
            this.collapsed = true;

            this.expanded = false;

            this._changeDetectorRef.markForCheck();
          }

          if (this.name === 'menu') {
            this.rootElement.classList.add(this.menuClass);
            this.rootElement.classList.remove(
              'vertical-overlay-menu',
              'menu-hide'
            );
          }

          this._hideOverlay();
        } else {
          this.iscollapsibleSidebar = false;

          this.expand();

          this.isOpened = false;

          this.openedChangedEvent.emit(this.isOpened);

          this.rootElement.classList.remove(this.menuClass);
          this.rootElement.classList.add('vertical-overlay-menu');

          this._hideSidebar();
        }

        this._wasCollapsible = isCollapsible;
      });
  }

  private _defaultCollapsed(): void {
    if (!this.collapsed) {
      return;
    }

    if (!this.isOpened) {
      return;
    }

    this.collapse();
  }

  private _showOverlay(): void {
    this._overlay = this._renderer.createElement('div');

    this._overlay?.classList.add(this.overlayClass);
    this._overlay?.classList.add('show');

    if (!this.overlayVisibility) {
      this._overlay?.classList.add('bg-transparent');
    }

    this._renderer.appendChild(
      this._elementRef.nativeElement.parentElement,
      this._overlay
    );

    this._animationPlayer = this._animationBuilder
      .build([animate('300ms ease', style({ opacity: 1 }))])
      .create(this._overlay);

    this._animationPlayer.play();

    this._overlay?.addEventListener('click', () => {
      this.close();
    });
    this._changeDetectorRef.markForCheck();
  }

  private _hideOverlay(): void {
    if (!this._overlay) {
      return;
    }

    this._animationPlayer = this._animationBuilder
      .build([animate('300ms ease', style({ opacity: 0 }))])
      .create(this._overlay);

    this._animationPlayer.play();

    this._animationPlayer.onDone(() => {
      if (this._overlay) {
        this._overlay.parentNode?.removeChild(this._overlay);
        this._overlay = null;
      }
    });
    this._changeDetectorRef.markForCheck();
  }

  private _showSidebar(): void {
    if (this.name == 'menu') {
      this.rootElement.classList.add('menu-open');
      this.rootElement.classList.remove('menu-hide');
    } else {
      this._renderer.addClass(this._elementRef.nativeElement, 'show');
      if (this.overlayClass === 'modal-backdrop') {
        this.rootElement.classList.add('modal-open');
      }
    }

    this._changeDetectorRef.markForCheck();
  }

  private _hideSidebar(): void {
    if (this.name == 'menu') {
      this.rootElement.classList.remove('menu-open');
      this.rootElement.classList.add('menu-hide');
    } else {
      this._renderer.removeClass(this._elementRef.nativeElement, 'show');

      if (this.overlayClass === 'modal-backdrop') {
        this.rootElement.classList.remove('modal-open');
      }
    }

    this._changeDetectorRef.markForCheck();
  }

  public collapseTemporarily(): void {
    if (!this.collapsed) {
      return;
    }

    this.expanded = false;
    this.collapsed = true;

    this._changeDetectorRef.markForCheck();
  }

  public expandTemporarily(): void {
    if (!this.collapsed) {
      return;
    }

    this.expanded = true;
    this.collapsed = true;

    this._changeDetectorRef.markForCheck();
  }

  public collapse(): void {
    if (this.collapsed) {
      return;
    }

    this.collapsed = true;

    this._changeDetectorRef.markForCheck();
  }

  public expand(): void {
    if (!this.collapsed) {
      return;
    }

    this.collapsed = false;

    this._changeDetectorRef.markForCheck();
  }

  public toggleCollapsible(): void {
    if (this.collapsed) {
      this.expand();
    } else {
      this.collapse();
    }
  }

  public open(): void {
    if (this.isOpened || this.iscollapsibleSidebar) {
      return;
    }

    this._showSidebar();

    this._showOverlay();

    this.isOpened = true;

    this.openedChangedEvent.emit(this.isOpened);

    this._changeDetectorRef.markForCheck();
  }

  public close(): void {
    if (!this.isOpened || this.iscollapsibleSidebar) {
      return;
    }

    this._hideOverlay();

    this.isOpened = false;

    this.openedChangedEvent.emit(this.isOpened);

    this._hideSidebar();

    this._changeDetectorRef.markForCheck();
  }

  public toggleOpen(): void {
    if (this.isOpened) {
      this.close();
    } else {
      this.open();
    }
  }
}
